import { ReactElement, useEffect, useState, CSSProperties } from "react";
import { IAxisBand, IAxisLinear, IPoint, IStackerAreaChartConfig } from "./types";
import Chart from "./chart";
import { getTicks, useChartDimensions, useUniqueId } from "./chartUtils";
import AxisHorizontal from "./AxisHorizontal";
import AxisVertical from "./AxisVertical";
import * as d3 from "d3";
import Grid from "./grid";
import { getPaletteColor } from "../../configuration/configuration";
import HtmlTooltip from "./tooltip";
import Legend from "./legend";
import { formatNumber } from "../../system/utils";

interface IChartProps {
  chartConfig: IStackerAreaChartConfig;
  sx?: CSSProperties;
}

const StackedLineChart = ({ chartConfig, sx }: IChartProps): ReactElement => {
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: chartConfig.height ?? undefined,
    width: chartConfig.width ?? undefined,
    marginBottom: 40,
    marginLeft: 10,
    marginRight: 60,
    marginTop: chartConfig.showLegend  ? 20 : 10
  });
  const [ data, setData ] = useState<any[]>([]);


  useEffect(() => {

    const newData: any[] = [];

    const dict: any = {};

    chartConfig.series.forEach(s => {

      (s.value as IPoint[]).forEach(p => {

        if (!dict[p.x]) {
          dict[p.x] = {
            x: p.x
          };

          newData.push(dict[p.x]);
        }

        dict[p.x][s.name] = p.y;
      });
    });


    setData(newData);
  }, [chartConfig]);

  const chartID = useUniqueId("stacked-area-chart");

  const keys = chartConfig.series.map(s => s.name);

  const stackGenerator = d3.stack()
    .keys(keys)
    .order(d3.stackOrderNone);

  const layers = stackGenerator(data);

  const isBandX = "categories" in chartConfig.xAxis;

  const xScale = isBandX
    ? d3.scaleBand()
      .domain((chartConfig.xAxis as IAxisBand).categories)
      .range([0, dimensions.boundedWidth])
    : d3.scaleLinear()
      .domain([(chartConfig.xAxis as IAxisLinear).minimum, (chartConfig.xAxis as IAxisLinear).maximum])
      .range([0, dimensions.boundedWidth]);

  const extent = [0, d3.max(layers, layer => d3.max(layer, sequence => sequence[1])) ?? 0];

  const yScale = d3.scaleLinear()
    .domain(extent)
    .range([dimensions.boundedHeight, 0])
    .nice();

  const areaGenerator = d3.area()
    .x((sequence: any) => xScale(sequence.data.x) ?? 0)
    .y0(sequence => yScale(sequence[0]))
    .y1(sequence => yScale(sequence[1]));

  const xTicks = isBandX ? xScale.domain() : getTicks(xScale.domain() as number[], (chartConfig.xAxis as IAxisLinear).ticksCount);
  const yTicks = getTicks(yScale.domain() as number[], 3);
  const xOffset = isBandX ?  (xScale as d3.ScaleBand<string>).bandwidth() / 2 : 0;

  return (
    <div ref={ref} style={sx}>
      <Chart dimensions={dimensions} >
        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label={chartConfig.xAxis.name}
          ticks={xTicks}
          formatTick={chartConfig.xAxis.formatTick}
        />

        <AxisVertical
          chartID={chartID}
          dimensions={dimensions}
          scale={yScale}
          ticks={yTicks}
          label={chartConfig.yAxis.name}
          formatTick={s => `${s}`}/>

        { chartConfig.showLegend && <Legend chartID={chartID} series={chartConfig.series} width={dimensions.boundedWidth} /> }

        <Grid charID={chartID} xScale={xScale} yScale={yScale} dimensions={dimensions} xTicks={xTicks} yTicks={yTicks}/>

        <g transform={`translate(${xOffset}, 0)`}>
          { layers.map((layer, index) => {
            const path = areaGenerator(layer as any) ?? "";
            const item = chartConfig.series.find(x => x.name === layer.key);

            return(
              <path key={`${chartID}-area-${index}`} d={path} fill={item ? item.color : getPaletteColor()}/>
            );
          })}

          { layers.map((layer, index) => {
            const series = chartConfig.series[index];

            return layer.map((item, yIndex) => {
              const categoryName = (item.data as any).x;
              const value = (item.data as any)[series.name];
              const y = yScale(item[1]);
              const x = xScale(categoryName) ?? 0;

              if (value === 0) {
                return null;
              }

              return (
                <HtmlTooltip
                  key={`${chartID}-layer-${index}-series-${yIndex}`}
                  title={
                    <div className="display-flex-column">
                      <div className="display-flex-column align-items-center" >
                        <strong>{categoryName}</strong>
                      </div>
                      <div className="display-flex-row align-items-center" >
                        <div style={{ background: series.color, width: 10, height: 10, marginRight: 10 }} />
                        <strong className="flex-grow-1">{series.name}</strong>
                        <strong className="flex-grow-0">
                          {formatNumber(value, 2)}
                          {series.unit && <sub>{`[${series.unit}]`}</sub>}
                        </strong>
                      </div>
                    </div>
                  }>
                  <circle cx={x} cy={y} r="2" fill="white" stroke={series.color} strokeWidth={2} />
                </HtmlTooltip>
              );
            });
          })}
        </g>
      </Chart>
    </div>);
};

export default StackedLineChart;
